import { ArgoContext } from '@amzn/awsscm-argo-core/argo';

import { ApolloClient, ApolloLink, from, HttpLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import { onError } from "@apollo/client/link/error";
import { get, startsWith } from 'lodash';

import { MAX_RESPONSE_TIME } from '../constants';

interface ContextualFetch {
  (input: Request | string, init?: RequestInit): Promise<Response>;
  context?: ArgoContext;
}

const client = new ApolloClient({
  cache: new InMemoryCache({}),
  defaultOptions: {
    query: {
      errorPolicy: 'none',
    },
    mutate: {
      errorPolicy: 'none',
    },
  },

  queryDeduplication: true,
});

let invoked = false;

function getApolloClient(context: ArgoContext): ApolloClient<NormalizedCacheObject> {

  if (!invoked && context) {
    invoked = true;
    const fetch: ContextualFetch = (input, init?) => {
      if (fetch.context) {
        return fetch.context.fetchWithAwsAuth(input, init);
      }
      return Promise.reject(new Error('ArgoAppContext not set'));
    };

    context.logger.info(`starting the links and context from getApolloClient`);

    const graphV2Link = new HttpLink({
      uri: '/graphV2',
      fetch,
    });

    const networkErrorsPublisher = context.initialMetricsPublisher.newChildActionPublisherForMethod(
      'networkErrors'
    );
    const graphQLErrorsPublisher = context.initialMetricsPublisher.newChildActionPublisherForMethod(
      'graphQLErrors'
    );

    const latencyPublisher = context.initialMetricsPublisher.newChildActionPublisherForMethod(
      'latency'
    );

    const consoleLink = new ApolloLink((operation, forward) => {
      context.logger.info(`starting request for ${operation.operationName}`);
      return forward(operation).map((data) => {
        context.logger.info(`ending request for ${operation.operationName}`);
        return data;
      })
    })

    const roundTripLink = new ApolloLink((operation, forward) => {
      // Called before operation is sent to server
      operation.setContext({ start: new Date() });

      return forward(operation).map((data) => {
        const { operationName } = operation;
        const publisher = context.initialMetricsPublisher.newChildActionPublisherForMethod(
          operationName
        );

        const time = new Date() - operation.getContext().start;
        publisher.publishTimerMonitor('responseTime', time);
        context.logger.info(`Operation ${operation.operationName} took ${time} to complete`);

        if (time > MAX_RESPONSE_TIME) {
          publisher.publishCounterMonitor(`maxLatencyExceeded`, 1);
          latencyPublisher.publishCounterMonitor(`latency`, 1);
          context.logger.warn(`Operation ${operation.operationName} took longer than ${MAX_RESPONSE_TIME} to complete`);
        }

        return data;
      });
    });

    // Log any GraphQL errors or network error that occurred
    const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
      const { operationName } = operation;

      const publisher = context.initialMetricsPublisher.newChildActionPublisherForMethod(
        operationName
      );

      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) => {
          context.logger.error(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
          )
          publisher.publishCounterMonitor(`${operationName}-graphQLError`, 1);
          graphQLErrorsPublisher.publishCounterMonitor(`graphQLError`, 1);
        });
      }

      if (networkError) {
        const statusCode = get(networkError, 'statusCode')
        const notReachableError = startsWith(statusCode, '5');
        if (notReachableError) {
          publisher.publishCounterMonitor(`networkError-${statusCode}`, 1);
          networkErrorsPublisher.publishCounterMonitor(`networkError`, 1);
        }
        context.logger.error(`[Network error]: ${networkError}`);
      }
      client.setLink(from([errorLink, consoleLink, roundTripLink, graphV2Link]))
    });

    fetch.context = context;
    client.setLink(ApolloLink.from([errorLink, consoleLink, roundTripLink, graphV2Link]))
  }

  return client;
}


/** Gets an ApolloClient in exchange for an ArgoContext. */
export default getApolloClient
